import { useCallback, useEffect, useState } from 'react';

import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import cx from 'classnames';

import { useConfigContext } from 'lib/core/config';
import { useAuthContext } from 'lib/core/context/AuthProvider';

import { useAgentContext } from 'lib/common/contexts/AgentContext';
import { useInstanceContext } from 'lib/common/contexts/InstanceContext';
import { useLayout } from 'lib/common/contexts/layout/LayoutContext';

import useTrigger from 'lib/common/hooks/useTrigger';

import Icon from 'lib/common/components/Icon';
import Loader from 'lib/common/components/Loader';
import Text from 'lib/common/components/Text';

import connectGetter from 'lib/common/utils/connectGetter';
import convertMSToReadableTime from 'lib/common/utils/convertMSToReadableTime';

import getAgentMetrics from './api/getAgentMetrics';
import Queues from './components/Queues';
import { IAgentStatsProps, TAgentQueues, TAllQueuesStats, TQueuesStats } from './types/AgentStats';

import './agent-stats.scss';

const POLL_TIMEOUT_MS = 5000;

function getAgentQueues(agent: connect.Agent | null, queues) {
  const routingProfileQueues = connectGetter(agent, 'getRoutingProfile')?.queues || [];
  const agentQueuesIds = routingProfileQueues.map((queue) => queue.queueId.split('/').pop());

  if (!agentQueuesIds) {
    return;
  }

  return queues.filter(({ Id }) => agentQueuesIds.includes(Id)).map(({ Id, Name }) => ({ Id, name: Name }));
}

const AgentStats = ({ setLoaderLoading, setLoaderNotLoading }: IAgentStatsProps) => {
  const [allQueuesStats, setAllQueuesStats] = useState<TAllQueuesStats>({});
  const [queuesStats, setQueuesStats] = useState<TQueuesStats[]>([]);
  const [agentQueues, setAgentQueues] = useState<TAgentQueues[]>([]);
  const [loadTrigger, updateLoadTrigger] = useTrigger();
  const [firstLoad, setFirstLoad] = useState(true);
  const [loading, setLoading] = useState(false);

  const { isSoftphone } = useLayout();
  const { fetch_ } = useAuthContext();
  const { config } = useConfigContext();
  const { agent } = useAgentContext();
  const { allVisibleQueues } = useInstanceContext();

  const loadData = useCallback(async () => {
    if (!agentQueues.length) {
      return void setTimeout(() => updateLoadTrigger(), POLL_TIMEOUT_MS);
    }

    !firstLoad && setLoaderLoading?.();
    !firstLoad && setLoading(true);

    const { allQueuesStats, queuesStats } = await getAgentMetrics({ fetch_, config, agentQueues });

    if (firstLoad) {
      setFirstLoad(false);
    }

    !firstLoad && setLoaderNotLoading?.();
    !firstLoad && setLoading(false);

    setAllQueuesStats(allQueuesStats || {});
    setQueuesStats(queuesStats || []);

    setTimeout(() => updateLoadTrigger(), POLL_TIMEOUT_MS);
  }, [agentQueues, firstLoad]);

  useEffect(() => {
    if (!allVisibleQueues.length) {
      return;
    }

    setAgentQueues(getAgentQueues(agent, allVisibleQueues));
  }, [allVisibleQueues]);

  useEffect(() => {
    loadData();
  }, [loadTrigger]);

  const { contactsInQueues, oldestContactInQueues } = allQueuesStats;

  return (
    <div className={cx('agent-stats', { 'agent-stats--softphone': isSoftphone })}>
      {firstLoad && <Loader className="m-auto" />}
      {!firstLoad && (
        <div
          className={cx('agent-stats__container', { 'agent-stats__container--softphone': isSoftphone })}
          data-testid="agent-stats"
        >
          <div className={cx('agent-stats__container__header', { 'mb-40': !isSoftphone, 'mb-20': isSoftphone })}>
            <div className=" agent-stats__container__header__title">
              <Text htmlElement="h2" type="heading3">
                Total (All My Queues)
              </Text>
              {isSoftphone && loading && <Loader className="ml-5" small delayBeforeShow={0} />}
            </div>
            <div className="agent-stats__container__header__stats">
              <Text>
                In Queue:{' '}
                <Text className={cx({ 'ml-10': !isSoftphone, 'ml-5': isSoftphone })} type="heading1">
                  {contactsInQueues}
                </Text>
              </Text>
              <Text>
                Oldest:{' '}
                <Text className={cx({ 'ml-10': !isSoftphone, 'ml-5': isSoftphone })} type="heading1">
                  {oldestContactInQueues ? convertMSToReadableTime(oldestContactInQueues * 1000) : `00:00`}
                </Text>
              </Text>
            </div>
          </div>
          {Boolean(queuesStats.length) && (
            <div className="agent-stats__queues">
              <Queues queues={queuesStats} />
            </div>
          )}
          <div
            className={cx('agent-stats__container__warning', {
              'mt-30': !isSoftphone,
              'agent-stats__container__warning--softphone': isSoftphone,
            })}
          >
            <Icon icon={faInfoCircle} size={15} />
            <Text>
              Queues can take <strong>15{isSoftphone ? 's' : ' seconds'}</strong> to update
            </Text>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentStats;
